/**
 * Language Services.
 * @format
 */

import { safeCurrentLanguage } from '@services/drupal-services';

export type LanguageListProps = {
  tid: string;
  name: string;
  language_display_text: string;
  locale_flag: string;
  locale_language_code: string;
  locale_language_region: string;
};

export type CurrentLanguageProps = {
  language: string;
  flag: string;
};

/**
 * Get Region Name.
 *
 * @param {string} region
 *   Key for region name.
 *
 * @return {string}
 *   Full regional name.
 */
export const getRegionName = (region?: string): string => {
  if (region) {
    switch (region) {
      case 'americas':
        return 'Americas';
      case 'apac':
        return 'Asia-Pacific';
      case 'emia':
        return 'Africa, Europe & Middle-East';
    }
  }
  return '';
};

/**
 * Get Relative Path.
 *
 * @param {LanguageListProps[]} languageList
 *   Array of all locale names, ids, flags, etc.
 *
 * @return {string}
 *   Current localized relative path.
 */
export const getRelativeLanguagePath = (
  languageList?: LanguageListProps[]
): string => {
  let pathName = window.location.pathname;
  if (!languageList || languageList.length === 0) {
    return pathName;
  }

  languageList.forEach((language) => {
    const { locale_language_code: id } = language;
    const path = pathName.split('/');
    if (path[1] === id) {
      pathName = pathName.replace(`/${id}`, '');
    }
  });

  return pathName;
};

/**
 * Get Current Language.
 *
 * @param {LanguageListProps[]} languageList
 *   Array of all locale names, ids, flags, etc.
 *
 * @return {CurrentLanguageProps}
 *   Current flag and full language name.
 */
export const getCurrentLanguage = (
  languageList?: LanguageListProps[]
): CurrentLanguageProps => {
  if (!languageList || languageList.length === 0) {
    return { flag: 'no-flag', language: '' };
  }

  let currentLanguage = '';
  let flag = 'no-flag';
  const currentLanguageId =
    safeCurrentLanguage() === 'es-419' ? 'es-xl' : safeCurrentLanguage();

  languageList.forEach((language) => {
    const {
      language_display_text: translatedName,
      locale_language_code: languageCode,
      locale_flag: localeFlag,
    } = language;
    if (languageCode === currentLanguageId) {
      currentLanguage = translatedName;
      flag = localeFlag;
    }
  });
  return { language: currentLanguage, flag };
};
